import type { ColumnDef } from '@tanstack/react-table';
import type { DetailPointTransactionEntity } from 'services/openapi';
import React from 'react';
import {
  PointTransactionStatus,
  PointTransactionTypes,
  PointTransactionTypesColor,
  TransactionStatusColor,
} from 'configs/constants';
import { StringUtils, NumberUtils } from 'modules';
import { endOfDay, startOfDay, format } from 'date-fns';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'configs';
import { formatDateTime } from 'utils/helpers/helpers';

export type FilterType = {
  transactionCode?: string;
  transactionType?:
    | 'Tất cả'
    | 'Thanh toán hóa đơn-Tiền điện'
    | 'Nạp điểm'
    | 'Chuyển điểm'
    | 'Nhận điểm'
    | 'Hoàn điểm'
    | 'Rút điểm';
  transactionStatus:
    | 'Tất cả'
    | 'Thành công'
    | 'Thất bại'
    | 'Đã hủy'
    | 'Đang chờ hủy'
    | 'Đang xử lý';
  fromTime: string | number;
  toTime: string | number;
  fromUserSearchString?: string;
  toUserSearchString?: string;
  searchDetailContent?: string;
  refreshData: number;
  pageSize: number;
  pageIndex: number;
};

export const getInitFilterValues = (urlParams: any): FilterType => {
  return {
    transactionCode: urlParams?.get('transactionCode') ?? '',
    transactionStatus:
      urlParams?.get('transactionStatus') ?? PointTransactionStatus['Tất cả'],
    transactionType:
      urlParams?.get('transactionType') ?? PointTransactionTypes['Tất cả'],
    fromTime:
      urlParams?.get('fromTime') ?? startOfDay(new Date()).toUTCString(),
    toTime: urlParams?.get('toTime') ?? endOfDay(new Date()).toUTCString(),
    fromUserSearchString: urlParams?.get('fromUserSearchString') ?? '',
    toUserSearchString: urlParams?.get('toUserSearchString') ?? '',
    searchDetailContent: urlParams?.get('searchDetailContent') ?? '',
    refreshData: 0,
    pageSize: Number(urlParams?.get('pageSize') ?? DEFAULT_PAGE_SIZE),
    pageIndex: Number(urlParams?.get('pageIndex') ?? DEFAULT_PAGE_INDEX),
  };
};

// export const initFilterValues: FilterType = {
//   transactionCode: '',
//   transactionStatus: PointTransactionStatus['Tất cả'],
//   transactionType: PointTransactionTypes['Tất cả'],
//   fromTime: startOfDay(new Date()).toUTCString(),
//   toTime: endOfDay(new Date()).toUTCString(),
//   fromUserSearchString: '',
//   toUserSearchString: '',
//   searchDetailContent: '',
//   refreshData: 0,
// };

export const columnsDefined: ColumnDef<DetailPointTransactionEntity>[] = [
  {
    accessorKey: 'transactionCode',
    header: 'Mã giao dịch',
  },
  {
    accessorKey: 'transactionType',
    header: 'Loại giao dịch',
  },
  {
    accessorKey: 'serviceType',
    header: 'Loại dịch vụ',
  },
  {
    accessorKey: 'topUpProvider',
    header: 'Phương thức nạp',
  },
  {
    accessorKey: 'fromUserPhone',
    header: 'Bên chuyển',
    cell: (info) =>
      info.getValue()
        ? StringUtils.normalizedPhoneNumber(info.getValue() as string)
        : '',
  },
  {
    accessorKey: 'toUserPhone',
    header: 'Bên thụ hưởng',
    cell: (info) =>
      info.getValue()
        ? StringUtils.normalizedPhoneNumber(info.getValue() as string)
        : '',
  },
  {
    accessorKey: 'amount',
    header: 'Số tiền',
    cell: (info) => {
      return (
        <div
          className={
            PointTransactionTypesColor[
              info.row.original
                ?.transactionType as keyof typeof PointTransactionTypesColor
            ]
          }
        >
          {info.getValue()
            ? NumberUtils.toFormatNumber(Number(info.getValue()))
            : '0'}
        </div>
      );
    },
    // cell: (info) => (info.getValue() ? NumberUtils.toFormatNumber(Number(info.getValue())) : ''),
  },
  {
    accessorKey: 'transactionStatusName',
    header: 'Trạng thái',
    cell: (info) => (
      <div
        className={
          TransactionStatusColor[
            info.getValue() as keyof typeof TransactionStatusColor
          ]
        }
      >
        {String(info.getValue())}
      </div>
    ),
  },
  {
    accessorKey: 'transactionUpdAt',
    header: 'Thời gian',
    cell: (info) =>
      format(new Date(String(info.getValue())), 'HH:mm:ss dd/MM/yyyy'),
  },
];

export const convertVariables = (variables: FilterType) => {
  const { fromTime, toTime, searchDetailContent, ...rest } = variables;

  const newTimeFrom =
    typeof fromTime === 'string' ? new Date(fromTime).getTime() : fromTime;
  const newTimeTo =
    typeof toTime === 'string' ? new Date(toTime).getTime() : toTime;

  return {
    ...rest,
    fromTime: fromTime ? formatDateTime(Number(newTimeFrom)) : '',
    toTime: toTime ? formatDateTime(Number(newTimeTo), true) : '',
    ...(searchDetailContent ? { searchDetailContent } : {}),
    excutedOnlySelf: false,
  };
};

export const convertSelectStatusOptions = () => {
  return Object.keys(PointTransactionStatus).map((key: string) => {
    return {
      label: PointTransactionStatus[key as keyof typeof PointTransactionStatus],
      value: key,
    };
  });
};

export const convertSelectTransactionTypeOptions = () => {
  return Object.keys(PointTransactionTypes).map((key: string) => {
    return {
      label: PointTransactionTypes[key as keyof typeof PointTransactionTypes],
      value: key,
    };
  });
};

export const exportHeaders = [
  'STT',
  'Mã giao dịch',
  'Loại giao dịch',
  'Loại dịch vụ',
  'Bên chuyển',
  'Bên thụ hưởng',
  'Số tiền',
  'Trạng thái',
  'Thời gian',
];
